// @ts-nocheck
/**
 * YMQ Custom — Shipping Fee Auto Select
 *
 * Lắng nghe event `ymq:shipping-calculated` (dispatched bởi
 * <form-check-address> sau khi gọi /price/calculate). Khi nhận được
 * distance_km từ event, tự động chọn option phí ship phù hợp trong
 * `.shipping_fee_auto_select` dropdown và khóa dropdown lại.
 *
 * Flow:
 *  1. <form-check-address> tự gọi OSM lấy lat/lon từ form data
 *  2. <form-check-address> POST tới /price/calculate với lat/lon + address
 *  3. Sau khi nhận response, dispatch `ymq:shipping-calculated` với:
 *       detail.distance_km   (number)
 *       detail.full_address  (string, để hiển thị dưới label)
 *  4. Module này bắt event → match distance với option → auto-select + lock
 *
 * Phụ thuộc:
 *  - 1-ymq-namespace.js phải được load trước.
 *  - css/ymq-shipping-fee.css phải được include (chứa style lock/loading).
 *
 * Public API (gắn vào window.YMQCustom):
 *  - YMQCustom.autoSelectShippingFee()     → bắt đầu lắng nghe event
 *  - YMQCustom.unlockShippingWrapper(el)   → mở khóa 1 wrapper
 *  - YMQCustom.unlockAllShippingWrappers() → mở khóa tất cả wrapper
 *
 * State riêng (giữ private trong IIFE):
 *  - isShippingApplied:  cờ tránh apply liên tục cho 1 distance đã apply
 *                        (reset khi nhận event mới với distance khác)
 *  - globalDistanceKm:   distance gần nhất nhận được từ event
 *  - globalFullAddress:  address gần nhất nhận được từ event
 */

(function () {
  const YMQCustom = window.YMQCustom;
  if (!YMQCustom) {
    console.error(
      "[YMQCustom] Namespace chưa được khởi tạo. Hãy load 1-ymq-namespace.js trước.",
    );
    return;
  }

  // (Module trước có dùng SHIPPING_API_URL & SHIPPING_LOG từ YMQCustom.config
  // để gọi API trực tiếp; flow mới gọi API qua <form-check-address>.action
  // nên không cần nữa.)

  // ── State riêng của module ──
  let isShippingApplied = false;
  let globalDistanceKm = null;
  let globalFullAddress = null;

  // ============================================================
  // ENTRY POINT — lắng nghe event từ <form-check-address>
  // ============================================================
  // Flow mới (May 2026):
  //  - <form-check-address> tự gọi OSM lấy lat/lon từ địa chỉ trong form
  //  - Submit form tới /price/calculate với payload (đã có lat/lon)
  //  - Sau khi nhận response, form dispatch event `ymq:shipping-calculated`
  //    với detail = { distance_km, full_address, ... }
  //  - Module này lắng nghe event và auto-select dropdown trong modal.
  //
  // KHÔNG còn đọc <script id="CustomerDefaultAddress"> — form là single
  // source of truth, vừa cho customer đã login (auto-populate từ Liquid)
  // vừa cho user mới fill thủ công.
  function autoSelectShippingFee() {
    document.addEventListener("ymq:shipping-calculated", (e) => {
      const detail = e.detail || {};
      const distanceKm = detail.distance_km;
      const fullAddress = detail.full_address;

      if (typeof distanceKm !== "number") return;

      globalDistanceKm = distanceKm;

      if (fullAddress) globalFullAddress = fullAddress;
      // Reset cờ để có thể re-apply khi user submit lại form với address khác
      isShippingApplied = false;

      applyToAllWrappers();
    });
  }

  // ── Apply distance đã có vào tất cả wrapper trong DOM ──
  function applyToAllWrappers() {
    if (isShippingApplied) return;
    if (typeof globalDistanceKm !== "number") return;

    const wrappers = document.querySelectorAll(".shipping_fee_auto_select");
    if (wrappers.length === 0) {
      document.querySelector(".ymq-fake-open-btn").disabled = true;
      // Wrapper chưa render → đợi xuất hiện rồi apply
      waitForElement(".shipping_fee_auto_select", 10000)
        .then(() => {
          document
            .querySelectorAll(".shipping_fee_auto_select")
            .forEach((w) => processWrapper(w, globalDistanceKm));
          isShippingApplied = true;
        })
        .catch(() => {});
      return;
    }

    // Reset lock state để có thể re-apply (vì có thể đã lock từ lần trước)
    wrappers.forEach((wrapper) => unlockShippingWrapper(wrapper));

    wrappers.forEach((wrapper) => processWrapper(wrapper, globalDistanceKm));
    isShippingApplied = true;
  }
  applyToAllWrappers();
  // ============================================================
  // WRAPPER PROCESSING
  // ============================================================

  // ── Xử lý 1 wrapper riêng lẻ ──
  function processWrapper(wrapper, distanceKm) {
    // Hiển thị loading ngay khi bắt đầu xử lý wrapper này
    showShippingLoading(wrapper);

    // Nếu wrapper hoặc group cha đang ẩn (class `hidden`), YMQ sẽ không render
    // dropdown panel khi mainSelectBox.click() → tryClickYMQ fail.
    // → Tạm thời show hidden ancestor bằng inline style off-screen.
    // Sau khi xong, xoá inline style để CSS .hidden tiếp tục có hiệu lực.
    const hiddenAncestors = [];
    let cur = wrapper;
    while (cur && cur !== document.body) {
      if (cur.classList && cur.classList.contains("hidden")) {
        hiddenAncestors.push(cur);
        // Đẩy ra ngoài viewport để user không thấy dropdown nhấp nháy.
        cur.style.cssText =
          "display: block !important; position: absolute !important; left: -99999px !important; top: 0 !important;";
      }
      cur = cur.parentElement;
    }

    function restoreHidden() {
      hiddenAncestors.forEach((el) => {
        el.style.cssText = "";
      });
    }

    // Tìm select của shipping fee — name bắt đầu bằng "配送料" để chắc chắn
    const selectEl =
      wrapper.querySelector('select[name^="配送料"]') ||
      wrapper.querySelector("select.ymq-dropdown");
    if (!selectEl) {
      hideShippingLoading(wrapper);
      restoreHidden();
      return;
    }
    if (selectEl.dataset.autoSelected === "1") {
      hideShippingLoading(wrapper);
      restoreHidden();
      return;
    }

    const matchedOption = findOptionBySkuDistance(selectEl, distanceKm);
    if (!matchedOption) {
      hideShippingLoading(wrapper);
      restoreHidden();
      return;
    }

    applySelectionAndLock(
      wrapper,
      selectEl,
      matchedOption,
      distanceKm,
      restoreHidden,
    );
    selectEl.dataset.autoSelected = "1";
  }

  // ── Match distance với option dropdown ──
  function findOptionByDistance(selectEl, distanceKm) {
    const parsed = [...selectEl.options]
      .filter((opt) => opt.value && !opt.dataset.placeholder)
      .map((opt) => {
        const v = opt.value.trim();
        const num = parseFloat(v.replace(/[^\d.]/g, ""));
        let type = "eq";
        if (v.startsWith("<")) type = "lt";
        else if (v.startsWith(">")) type = "gt";
        return { opt, num, type };
      })
      .filter((x) => !isNaN(x.num));

    const ltMatch = parsed
      .filter((x) => x.type === "lt" && distanceKm < x.num)
      .sort((a, b) => a.num - b.num)[0];
    if (ltMatch) return ltMatch.opt;

    const gtMatch = parsed
      .filter((x) => x.type === "gt" && distanceKm > x.num)
      .sort((a, b) => b.num - a.num)[0];
    if (gtMatch) return gtMatch.opt;

    return (
      parsed.sort(
        (a, b) => Math.abs(a.num - distanceKm) - Math.abs(b.num - distanceKm),
      )[0]?.opt || null
    );
  }

  function findOptionBySkuDistance(selectEl, distanceKm) {
    // 1. Chuyển đổi options thành array và lọc bỏ các placeholder
    const parsed = [...selectEl.options]
      .filter((opt) => !opt.dataset.placeholder)
      .map((opt) => {
        // Lấy giá trị từ thuộc tính 'ymq-option-sku'
        const sku = opt.getAttribute("ymq-option-sku") || "";
        // Dùng Regex để tách lấy phần số sau chữ SHIP_
        const match = sku.match(/SHIP_(\d+)/);
        const num = match ? parseFloat(match[1]) : NaN;

        return { opt, num };
      })
      .filter((x) => !isNaN(x.num))
      .sort((a, b) => a.num - b.num); // Đảm bảo danh sách luôn được sắp xếp tăng dần

    if (parsed.length === 0) return null;

    // 2. Tìm option đầu tiên có khoảng cách cấu hình lớn hơn hoặc bằng distanceKm
    const matched = parsed.find((x) => x.num >= distanceKm);

    // 3. Trả về kết quả
    // Nếu tìm thấy (ví dụ 15km -> lấy SHIP_20), trả về option đó.
    // Nếu distanceKm lớn hơn tất cả các cấu hình (ví dụ 35km tương đương vượt quá SHIP_30),
    // thì hàm sẽ lấy option lớn nhất cuối cùng (parsed[parsed.length - 1].opt).
    return matched ? matched.opt : parsed[parsed.length - 1].opt;
  }
  // ── Set value + trigger YMQ click + lock ──
  function applySelectionAndLock(
    wrapper,
    selectEl,
    option,
    distanceKm,
    restoreHidden,
  ) {
    const mainSelectBox = wrapper.querySelector(".ymq-app-select-main");
    const dataId = mainSelectBox?.getAttribute("data-id");

    if (!mainSelectBox) {
      hideShippingLoading(wrapper);
      restoreHidden?.();
      return;
    }

    // 1) Click để mở dropdown, kích hoạt YMQ render HTML
    if (mainSelectBox.getAttribute("aria-expanded") !== "true") {
      mainSelectBox.click();
    }

    // 2) Chờ HTML render và click
    let attempts = 0;
    const tryClickYMQ = setInterval(() => {
      attempts++;
      const escapedValue = CSS.escape(option.value);
      const selectors = dataId
        ? `.ymq-app-select-content[data-id="${dataId}"] [ymq-option-item-value="${escapedValue}"]`
        : `[ymq-option-item-value="${escapedValue}"]`;

      const optionItems = document.querySelectorAll(selectors);

      if (optionItems.length > 0) {
        clearInterval(tryClickYMQ); // Dừng vòng lặp

        // Bấm chọn item
        optionItems.forEach((item) => item.click());

        // 3) Đợi 300ms cho YMQ đóng dropdown và cập nhật giá xong rồi mới khóa CSS
        setTimeout(() => {
          lockShippingWrapper(wrapper, selectEl, option.value);
          // Ẩn loading sau khi đã chọn + lock xong hoàn toàn
          hideShippingLoading(wrapper);
          // Restore inline style để CSS .hidden tiếp tục có hiệu lực
          restoreHidden?.();
        }, 300);
      } else if (attempts >= 20) {
        // Quá 2 giây không tìm thấy
        clearInterval(tryClickYMQ);
        // Đóng dropdown nếu bị kẹt
        if (mainSelectBox.getAttribute("aria-expanded") === "true") {
          mainSelectBox.click();
        }
        // Ẩn loading khi timeout để user còn thao tác được
        hideShippingLoading(wrapper);
        restoreHidden?.();
      }
    }, 100);
  }

  // ============================================================
  // LOCK & UI HELPERS
  // ============================================================

  // ── Mở khóa wrapper + dropdown panel + dừng observer giữ giá trị ──
  function unlockShippingWrapper(wrapper, selectEl) {
    if (!wrapper) return;

    if (wrapper._shippingFeeLockObs) {
      wrapper._shippingFeeLockObs.disconnect();
      delete wrapper._shippingFeeLockObs;
    }

    wrapper.classList.remove("shipping-fee-locked");

    const dataId = wrapper
      .querySelector(".ymq-app-select-main")
      ?.getAttribute("data-id");
    if (dataId) {
      document
        .querySelectorAll(`.ymq-app-select-content[data-id="${dataId}"]`)
        .forEach((el) => el.classList.remove("shipping-fee-locked-panel"));
    }

    const select =
      selectEl ||
      wrapper.querySelector('select[name^="配送料"]') ||
      wrapper.querySelector("select.ymq-dropdown");
    if (select) {
      delete select._lastFired;
      delete select.dataset.autoSelected;
    }
  }

  function unlockAllShippingWrappers() {
    document
      .querySelectorAll(".shipping_fee_auto_select")
      .forEach((wrapper) => unlockShippingWrapper(wrapper));
  }

  // ── Khoá wrapper + dropdown panel (CHỈ DÙNG CSS, KHÔNG DÙNG DISABLED) ──
  function lockShippingWrapper(wrapper, selectEl, lockedValue) {
    unlockShippingWrapper(wrapper, selectEl);

    wrapper.classList.add("shipping-fee-locked");
    // TUYỆT ĐỐI KHÔNG DÙNG: selectEl.disabled = true;

    const lockPanels = () => {
      const dataId = wrapper
        .querySelector(".ymq-app-select-main")
        ?.getAttribute("data-id");
      if (!dataId) return;
      document
        .querySelectorAll(`.ymq-app-select-content[data-id="${dataId}"]`)
        .forEach((el) => el.classList.add("shipping-fee-locked-panel"));
    };
    lockPanels();

    let scheduled = false;
    const wrapperObs = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        wrapperObs.disconnect();

        if (!wrapper.classList.contains("shipping-fee-locked")) {
          wrapper.classList.add("shipping-fee-locked");
        }

        // ── CHỐT CHẶN INFINITY LOOP (THROTTLE) ──
        if (selectEl.value !== lockedValue) {
          selectEl.value = lockedValue;

          // Dùng timestamp để chặn bắn event liên tục (Ping-Pong loop)
          // Chỉ cho phép bắn event change tối đa 1 lần mỗi 1.5 giây.
          const now = Date.now();
          if (!selectEl._lastFired || now - selectEl._lastFired > 1500) {
            selectEl._lastFired = now;
            // Bắn event để YMQ nhận diện (cứu lại tính năng auto-load)
            selectEl.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }

        wrapperObs.observe(wrapper, {
          attributes: true,
          attributeFilter: ["class"],
          subtree: false,
        });
      });
    });

    wrapper._shippingFeeLockObs = wrapperObs;
    wrapperObs.observe(wrapper, {
      attributes: true,
      attributeFilter: ["class"],
      subtree: false,
    });
  }

  // ============================================================
  // LOADING OVERLAY HELPERS
  // ============================================================

  // ── Hiển thị block loading lên wrapper khi đang xử lý ──
  function showShippingLoading(wrapper) {
    if (!wrapper) return;
    // Tránh tạo overlay trùng nếu đã đang loading
    if (wrapper.querySelector(":scope > .shipping-fee-loading-overlay")) return;

    wrapper.classList.add("shipping-fee-loading");

    const overlay = document.createElement("div");
    overlay.className = "shipping-fee-loading-overlay";
    overlay.innerHTML = `
            <div class="shipping-fee-loading-spinner" aria-hidden="true"></div>
            <div class="shipping-fee-loading-text">配送料を計算中です...</div>
        `;
    wrapper.appendChild(overlay);
  }

  // ── Ẩn block loading khỏi wrapper sau khi xử lý xong / lỗi ──
  function hideShippingLoading(wrapper) {
    if (!wrapper) return;
    wrapper.classList.remove("shipping-fee-loading");
    wrapper.querySelector(":scope > .shipping-fee-loading-overlay")?.remove();
  }

  // ============================================================
  // GENERIC HELPERS
  // ============================================================

  // ── Đợi element xuất hiện ──
  function waitForElement(selector, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(selector);
      if (existing) return resolve(existing);

      const obs = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          obs.disconnect();
          resolve(el);
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });

      setTimeout(() => {
        obs.disconnect();
        reject(new Error(`timeout waiting for ${selector}`));
      }, timeout);
    });
  }

  // ── Expose public API ──
  YMQCustom.autoSelectShippingFee = autoSelectShippingFee;
  YMQCustom.unlockShippingWrapper = unlockShippingWrapper;
  YMQCustom.unlockAllShippingWrappers = unlockAllShippingWrappers;
})();
